import { useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';

import { storage } from '../lib/firebase.js';
import { logger } from '../lib/logger.js';
import { useI18n } from '../lib/use-i18n.js';

/**
 * Sprint 87 — Super-admin convenience: turn a `kyc.icScanRef` Storage
 * path into a clickable "Open document" button that resolves a fresh
 * download URL on click.
 *
 * Why on-click instead of pre-resolving? Storage download URLs are
 * long-lived (effectively static for the object's lifetime), so a
 * fetched URL would persist across renders unnecessarily; resolving
 * lazily keeps the row light and lets a super-admin who never opens
 * the doc avoid the extra HTTP call entirely.
 *
 * Auth: storage.rules grants read to either the persona themselves
 * (via the `agentId` / `recruiterId` claim) or to any super-admin
 * (via the brand-scoped `superadmin` claim — fixed in this sprint).
 */
export function KycScanLink({ storagePath }: { readonly storagePath: string }) {
  const { t } = useI18n();
  const [stage, setStage] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; url: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function resolve() {
    setStage({ kind: 'loading' });
    try {
      const url = await getDownloadURL(ref(storage, storagePath));
      setStage({ kind: 'ready', url });
      // Open in a new tab once we have the URL.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      logger.error('kyc scan url resolve failed', { storagePath, error: String(e) });
      setStage({
        kind: 'error',
        message: String((e as Error).message ?? 'Could not open document.'),
      });
    }
  }

  if (stage.kind === 'ready') {
    return (
      <a href={stage.url} target="_blank" rel="noopener noreferrer" style={linkButtonStyle}>
        {t('dashboard.kycScan.openDocument')} ↗
      </a>
    );
  }

  return (
    <div style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center' }}>
      <button
        type="button"
        onClick={() => void resolve()}
        disabled={stage.kind === 'loading'}
        style={{
          ...linkButtonStyle,
          opacity: stage.kind === 'loading' ? 0.6 : 1,
          cursor: stage.kind === 'loading' ? 'wait' : 'pointer',
        }}
      >
        {stage.kind === 'loading' ? t('dashboard.kyc.opening') : t('dashboard.kyc.viewScan')}
      </button>
      {stage.kind === 'error' ? (
        <span
          role="alert"
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-accent)',
          }}
        >
          {stage.message}
        </span>
      ) : null}
    </div>
  );
}

const linkButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid rgba(13,122,79,0.32)',
  background: 'var(--color-surface)',
  color: 'var(--color-primary)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  textDecoration: 'none',
  cursor: 'pointer',
};
